import { useFeatureFlags } from '@chronosphereio/core';
import { ReactNode, useMemo } from 'react';
import { encodeVariableValue, VariableStateMap } from '@/core';
import {
  TemplateVariableContextProvider,
  timeRangeToQueryString,
  useTemplateVariableValues,
  useTimeRange,
} from '../../../plugin-system';
import { getURLQueryParamName } from '../TemplateVariableProvider/query-params';

export const VizContextVariableNames = {
  StartTime: '__start_time',
  EndTime: '__end_time',
  TimeRangeQueryString: '__timeRangeQueryString',
  VariablesQueryString: '__variablesQueryString',
  VizContextQueryString: '__visualizationContextQueryString',
};

const getVariablesQueryString = (variables: VariableStateMap) => {
  const variableParams = new URLSearchParams();

  for (const [key, variable] of Object.entries(variables)) {
    const value = encodeVariableValue(variable.value);
    variableParams.append(getURLQueryParamName(key), value === null ? '' : value);
  }

  return variableParams.toString();
};

export const VizContextVariablesProvider = ({ children }: { children: ReactNode }) => {
  const variableContext = useTemplateVariableValues();
  const { absoluteTimeRange, timeRange } = useTimeRange();
  const { enableNewTimeRangeQueryParams } = useFeatureFlags();

  const derivedCtx = useMemo(() => {
    const timeRangeQueryString = timeRangeToQueryString(timeRange, { enableNewTimeRangeQueryParams });
    const variablesQueryString = getVariablesQueryString(variableContext);

    return {
      state: {
        __start_time: { value: String(absoluteTimeRange.start.valueOf()), loading: false },
        __end_time: { value: String(absoluteTimeRange.end.valueOf()), loading: false },
        __timeRangeQueryString: { value: timeRangeQueryString, loading: false },
        __variablesQueryString: { value: variablesQueryString, loading: false },
        __visualizationContextQueryString: { value: `${timeRangeQueryString}&${variablesQueryString}`, loading: false },
      },
    };
  }, [timeRange, absoluteTimeRange, variableContext, enableNewTimeRangeQueryParams]);

  return <TemplateVariableContextProvider value={derivedCtx}>{children}</TemplateVariableContextProvider>;
};
